import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { CheckCircle2, Info } from "lucide-react"

export interface StudioV2DoneScreenProps {
  /**
   * WHY this screen is showing, because "no next `ready` row in the loaded
   * list" (`nextOpenDeclaration` returning null) means two very different
   * things depending on how it was reached:
   *
   *  - "completed": the reviewer just approved (or requested) THIS
   *    declaration and no `ready` row remains loaded — a real completion.
   *  - "no-more-loaded" (the default): Überspringen advanced and found
   *    nothing — but skipping persists nothing, so the skipped declaration
   *    is still `ready` server-side. This is NOT "the inbox is empty", only
   *    "nothing further is loaded right now" (the skipped row itself, or a
   *    genuinely later one sitting beyond the loaded page).
   *
   * Defaults to the honest, non-committal framing, so a caller that forgets to
   * pass a reason under-claims rather than over-claims. `StudioV2Layout` passes
   * both: "completed" from the post-approve path, "no-more-loaded" from skip.
   */
  reason?: "completed" | "no-more-loaded"
  onBackToList: () => void
}

/**
 * The terminal screen shown when no `lifecycleStatus === "ready"` declaration
 * remains LOADED after Übernehmen or Überspringen navigated forward and found
 * nowhere to go. Centered, one sentence, one way out — the same shape as
 * `ApprovedView`'s own terminal screens (no tab bar, no half-finished chrome).
 *
 * Never prints a total: `declarations` is the loaded page, not the server
 * total (a later `ready` row can sit beyond it) — inventing a count here
 * would just trade one wrong claim for another.
 */
export function StudioV2DoneScreen({
  reason = "no-more-loaded",
  onBackToList,
}: StudioV2DoneScreenProps) {
  const { translate } = useTranslation()
  const isCompleted = reason === "completed"

  return (
    <div className="sv2-root flex h-full min-h-0 flex-1 flex-col items-center justify-center bg-card px-6 text-center">
      {/* `sv2-donecard`, not `sv2-done` — that name is the status-pill
          modifier (`.sv2-tpill.sv2-done`), and the unscoped card rule
          inflated every accepted pill in the list. */}
      <div className="sv2-donecard">
        <div className="sv2-ok">
          {isCompleted ? (
            <CheckCircle2 size={28} className="sv2-ic" aria-hidden="true" />
          ) : (
            <Info size={28} className="sv2-ic" aria-hidden="true" />
          )}
        </div>
        <h2>
          {translate(
            isCompleted
              ? "MC_DECL_V2_DONE_TITLE"
              : "MC_DECL_V2_DONE_SKIPPED_TITLE",
          )}
        </h2>
        <p>
          {translate(
            isCompleted
              ? "MC_DECL_V2_DONE_SUMMARY"
              : "MC_DECL_V2_DONE_SKIPPED_SUMMARY",
          )}
        </p>
        <button
          type="button"
          className="sv2-btn sv2-btn-primary"
          onClick={onBackToList}
        >
          {translate("MC_DECL_V2_DONE_BACK_TO_LIST")}
        </button>
      </div>
    </div>
  )
}
